<script>
export const ALLERGIES_SCHEMA = {
    widgetType: 'allergiesList',
    label: 'Allergies',
    fields: [
        { name: 'name', type: 'string', required: true, description: 'Allergy name (e.g. Penicillin)' },
        { name: 'reaction', type: 'string', required: false, description: 'Reaction manifestation (e.g. Hives)' },
        {
            name: 'severity',
            type: 'choice',
            required: false,
            description: 'Severity level',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'high', label: 'High' },
                { value: 'unable-to-assess', label: 'Unable to assess' },
            ],
        },
        {
            name: 'category',
            type: 'choice',
            required: false,
            description: 'Allergen type',
            options: [
                { value: 'medication', label: 'Medication' },
                { value: 'food', label: 'Food' },
                { value: 'environment', label: 'Environment' },
                { value: 'biologic', label: 'Biologic' },
            ],
        },
    ],
};
</script>

<script setup>
import { getMedByName } from '@/common/api/rxnorm.api';
import * as permissions from '@/common/permissionConstants';
import BnDatePicker from '@/components/BnDatePicker.vue';
import BnDialog from '@/components/BnDialog.vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnToolbarBtn from '@/components/BnToolbarBtn';
import DataProvider from '@/components/DataProvider';
import { AllergyIntolerance, CodeableConcept } from '@/fhirworks';
import AllergyIntoleranceReaction from '@/fhirworks/AllergyIntoleranceReaction';
import { formatPartialDate } from '@/filters/dateTimeFilters';
import { useScriptSureClinicalSync } from '@/composables/api/scriptSure/useScriptSureClinicalSync';
import { useAuthStore } from '@/stores/auth';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { debounce } from 'lodash';
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { validateChoiceFields } from '@/components/questionnaires/widgets/widgetValidation';

const app = getCurrentInstance();
const isPortal = app.appContext.config.globalProperties?.isPortal;

const vuetifyDisplay = useDisplay();

const allergenTypes = [
    { icon: ['far', 'pills'], value: 'medication', title: 'Medication' },
    { icon: ['far', 'plate-utensils'], value: 'food', title: 'Food' },
    { icon: ['far', 'trees'], value: 'environment', title: 'Environment' },
    { icon: ['far', 'dna'], value: 'biologic', title: 'Biologic' },
];

const reactionCriticality = [
    { value: 'low', title: 'Low' },
    { value: 'high', title: 'High' },
    { value: 'unable-to-assess', title: 'Unable to assess' },
];

const authStore = useAuthStore();
const { isScriptSureEnabled, handleAllergiesSaved, getScriptSurePatientId, ensureScriptSurePatient, searchAllergen, NO_KNOWN_ALLERGY_CODE } = useScriptSureClinicalSync();

// Snackbar notification for ScriptSure missing patient data
const scriptSureSnackbar = ref(false);
const scriptSureSnackbarMessage = ref('');

const props = defineProps({
    title: String,
    printView: Boolean,
    isLocked: Boolean,
    modelValue: [Array, Object, String],
    variant: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'compact'].includes(value),
    },
    showInactive: {
        type: Boolean,
        default: false,
    },
    patient: {
        type: Object,
        default: null,
    },
});

const patientId = computed(() => props.patient?.id);

const isCompact = computed(() => props.variant === 'compact');
const displayLimit = 3;
const showAll = ref(false);

const readOnly = computed(() => props.isLocked || props.printView);

const canViewAllergies = computed(() => authStore.hasPermission([permissions.VIEW_ALLERGIES, permissions.MANAGE_ALLERGIES]) || isPortal);
const viewOnlyAllergies = computed(() => !authStore.hasPermission(permissions.MANAGE_ALLERGIES) && !isPortal && !readOnly.value);

const emit = defineEmits(['update:modelValue']);

const bnDialogRef = ref(null);
const allergiesDP = ref(null);
const allergiesData = ref([]);

// Edit dialog state
const editDialog = ref(false);
const editingAllergy = ref(null);
const originalAllergy = ref(null);
const isNewAllergy = ref(false);

const allergyForm = computed(() => ({
    allergenName: editingAllergy.value?.code?.text || '',
}));
const allergyValidations = {
    allergyForm: {
        allergenName: { required },
    },
};
const v$ = useVuelidate(allergyValidations, { allergyForm });

const allergiesQuery = computed(() => {
    if (props.isLocked && props.modelValue) {
        return { resource: props.modelValue };
    }

    return {
        query: { query: `patient=${patientId.value}`, resourceType: 'AllergyIntolerance' },
    };
});

const isAllergyInactive = (allergy) => {
    return allergy.clinicalStatus?.codingZero?.code === 'inactive';
};

const activeAllergies = computed(() => {
    return allergiesData.value.filter((allergy) => allergy.code?.codingZero?.code !== NO_KNOWN_ALLERGY_CODE && allergy.clinicalStatus?.codingZero.code === 'active');
});

const allRealAllergies = computed(() => {
    return allergiesData.value.filter((allergy) => allergy.code?.codingZero?.code !== NO_KNOWN_ALLERGY_CODE);
});

const displayedAllergies = computed(() => {
    let allergies = allRealAllergies.value.filter((allergy) => allergy.code?.text);

    if (!isCompact.value || !props.showInactive) {
        allergies = allergies.filter((allergy) => !isAllergyInactive(allergy));
    }

    return allergies;
});

// Count of inactive allergies (for parent component to know if menu should show)
const inactiveAllergiesCount = computed(() => {
    return allRealAllergies.value.filter((allergy) => allergy.code?.text && isAllergyInactive(allergy)).length;
});

// Show more/less logic for compact mode
const showMoreCount = computed(() => {
    if (!isCompact.value) return 0;
    const diff = displayedAllergies.value.length - displayLimit;
    return diff > 0 ? diff : 0;
});

const limitedAllergies = computed(() => {
    if (!isCompact.value || showAll.value) {
        return displayedAllergies.value;
    }
    return displayedAllergies.value.slice(0, displayLimit);
});

const showMore = () => {
    showAll.value = !showAll.value;
};

const hasNoAllergies = computed(() => {
    return allergiesData.value.some((allergy) => allergy.code?.codingZero?.code === NO_KNOWN_ALLERGY_CODE);
});

const hasAllergies = ref(null);

watch(
    () => hasAllergies.value,
    () => {
        if (hasAllergies.value) {
            if (!activeAllergies.value?.length) setAllergiesTrue();
            return;
        }
        if (allergiesData.value.find((allergy) => allergy.code?.codingZero?.code === NO_KNOWN_ALLERGY_CODE)) return;
        removeAllergiesData();
    },
);

watch(
    () => allergiesData.value,
    () => {
        if (allergiesData.value && !props.isLocked) {
            emit('update:modelValue', allergiesData.value);
        }
    },
    { immediate: true, deep: true },
);

const allergiesLoaded = () => {
    if (allergiesData.value.length) {
        // Check if there's a "no known allergies" entry
        const hasNoKnownAllergy = allergiesData.value.find((allergy) => allergy.code?.codingZero?.code === NO_KNOWN_ALLERGY_CODE);
        // Check if there are any real allergies
        const hasRealAllergies = allergiesData.value.some((allergy) => allergy.code?.codingZero?.code !== NO_KNOWN_ALLERGY_CODE);

        if (hasNoKnownAllergy) {
            hasAllergies.value = false;
        } else if (hasRealAllergies) {
            hasAllergies.value = true;
        }
        // If neither, leave as null (user hasn't answered yet)
    }
};

// Create a new empty allergy object
const createEmptyAllergy = () => {
    return new AllergyIntolerance({
        patient: { id: patientId.value, resourceType: 'Patient' },
        code: {
            text: '',
        },
        reaction: [
            {
                manifestation: [
                    {
                        text: '',
                    },
                ],
            },
        ],
        clinicalStatus: {
            coding: [
                {
                    code: 'active',
                    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                },
            ],
        },
        verificationStatus: {
            coding: [
                {
                    code: 'confirmed',
                    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
                },
            ],
        },
        recordedDate: new Date().toISOString(),
        recorder: authStore.aidboxPersonReference,
    });
};

// Open dialog to add a new allergy
const openAddDialog = () => {
    // In compact mode or when adding, ensure hasAllergies is set
    if (hasAllergies.value !== true) {
        hasAllergies.value = true;
    }

    allergyList.value = [];
    searchRxNorm.value = null;

    editingAllergy.value = createEmptyAllergy();
    isNewAllergy.value = true;
    v$.value.allergyForm.$reset();
    editDialog.value = true;
};

// Open dialog to edit an existing allergy
const openEditDialog = (allergy) => {
    if (viewOnlyAllergies.value || readOnly.value) return;

    allergyList.value = [];
    searchRxNorm.value = null;
    // Store reference to original and work with a deep copy
    // Use JSON parse/stringify to properly clone FHIR resource objects
    originalAllergy.value = allergy;
    const allergyJson = typeof allergy.toJSON === 'function' ? allergy.toJSON() : JSON.parse(JSON.stringify(allergy));
    editingAllergy.value = new AllergyIntolerance(allergyJson);
    // Ensure category array exists for the v-model binding
    if (!editingAllergy.value.category) {
        editingAllergy.value.category = [];
    }
    // Ensure reaction array exists for the v-model binding
    if (!editingAllergy.value.reaction?.[0]?.manifestation) {
        editingAllergy.value.reaction = [{ manifestation: [{ text: '' }] }];
    }
    isNewAllergy.value = false;
    v$.value.allergyForm.$reset();
    editDialog.value = true;
};

// Save the allergy (add new or update existing)
const saveAllergy = async () => {
    v$.value.allergyForm.$touch();
    if (!(await v$.value.allergyForm.$validate())) return;

    if (isNewAllergy.value) {
        await allergiesDP.value.save(editingAllergy.value);
    } else {
        // Save the edited copy directly - it has the same id so DataProvider will update
        // Then replace the original in the data array with the updated version
        await allergiesDP.value.save(editingAllergy.value);
        // Find and replace the original in the allergies array
        const index = allergiesData.value.findIndex((a) => a.id === editingAllergy.value.id);
        if (index !== -1) {
            allergiesData.value[index] = editingAllergy.value;
        }
    }

    editDialog.value = false;
    editingAllergy.value = null;
    originalAllergy.value = null;
};

// Cancel editing
const cancelEdit = () => {
    editDialog.value = false;
    editingAllergy.value = null;
    originalAllergy.value = null;
};

const addItem = async (data) => {
    const invalidChoices = validateChoiceFields(data, ALLERGIES_SCHEMA);
    if (invalidChoices) return invalidChoices;

    const allergy = createEmptyAllergy();
    if (data.name) allergy.code.text = data.name;
    if (data.reaction) allergy.reaction[0].manifestation[0].text = data.reaction;
    if (data.severity) allergy.criticality = data.severity;
    if (data.category) allergy.category = [data.category];
    await allergiesDP.value.save(allergy);

    const noKnownEntry = allergiesData.value.find((a) => a.code?.codingZero?.code === NO_KNOWN_ALLERGY_CODE);
    if (noKnownEntry) {
        await allergiesDP.value.deleteItem(noKnownEntry);
    }

    hasAllergies.value = true;
    return { success: true };
};

const getItems = () => {
    return allRealAllergies.value.map((allergy) => ({
        name: allergy.code?.text || '',
        reaction: allergy.reaction?.[0]?.manifestation?.[0]?.text || '',
        severity: allergy.criticality || '',
        category: allergy.category?.[0] || '',
        status: allergy.clinicalStatus?.codingZero?.code || '',
    }));
};

defineExpose({
    openAddDialog,
    openEditDialog,
    inactiveAllergiesCount,
    showMoreCount,
    showAll,
    showMore,
    v$,
    addItem,
    getItems,
});

const _registerWidgetApi = inject('registerWidgetApi', null);
const _unregisterWidgetApi = inject('unregisterWidgetApi', null);

onMounted(() => {
    _registerWidgetApi?.('allergiesList', { addItem, getItems, schema: ALLERGIES_SCHEMA });
});

onBeforeUnmount(() => {
    _unregisterWidgetApi?.('allergiesList');
});

const setAllergiesTrue = async () => {
    let noKnownAllergyResource = allergiesData.value.find((allergy) => allergy?.code?.codingZero?.code === NO_KNOWN_ALLERGY_CODE);

    if (noKnownAllergyResource) {
        await allergiesDP.value.deleteItem(noKnownAllergyResource);
    }

    // If no allergies exist, open the add dialog automatically
    if (!allergiesData.value.length) {
        openAddDialog();
    }
};

const removeAllergiesData = async () => {
    let noKnownAllergyResource = new AllergyIntolerance({
        patient: { id: patientId.value, resourceType: 'Patient' },
        clinicalStatus: {
            coding: [
                {
                    code: 'active',
                    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                },
            ],
        },
        verificationStatus: {
            coding: [
                {
                    code: 'confirmed',
                    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
                },
            ],
        },
        code: {
            coding: [
                {
                    code: NO_KNOWN_ALLERGY_CODE,
                    system: 'http://snomed.info/sct',
                    display: 'No known allergies',
                },
            ],
        },
        status: 'active',
    });
    // if there is regular allergies that aren't "noKnownAllergies" then save without the prompt
    // also skip dialog if there's only 1 allergy with blank code.text
    const hasNoRealAllergies = !allergiesData.value.find((resource) => resource.code?.coding?.[0]?.code !== NO_KNOWN_ALLERGY_CODE && resource?.id);
    const hasSingleBlankAllergy = allergiesData.value.length === 1 && !allergiesData.value[0]?.code?.text;

    if (hasNoRealAllergies || hasSingleBlankAllergy) {
        for (let resource of allergiesData.value) {
            allergiesDP.value.deleteItem(resource);
        }

        await allergiesDP.value.save(noKnownAllergyResource);
        return;
    }
    const options = {
        header: 'Warning',
        message: 'This will delete all existing allergies, Continue?',
        confirm: 'Delete',
        cancel: 'Cancel',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) {
            hasAllergies.value = true;
            return;
        }

        for (let allergy of allergiesData.value) {
            allergiesDP.value.deleteItem(allergy);
        }
        await allergiesDP.value.save(noKnownAllergyResource);
    });
};

const isEditingAllergyInactive = computed(() => {
    return editingAllergy.value ? isAllergyInactive(editingAllergy.value) : false;
});

const inactivateAllergy = (allergy) => {
    const options = {
        header: 'Archive',
        message: 'Are you sure you want to archive this allergy?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        // Set clinicalStatus to inactive
        const allergyToInactivate = originalAllergy.value || allergy;
        allergyToInactivate.clinicalStatus = {
            coding: [
                {
                    code: 'inactive',
                    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                },
            ],
        };
        await allergiesDP.value.save(allergyToInactivate);

        // ScriptSure sync is handled automatically by the DataProvider @saved event
        // (handleAllergySaved), which will pick up the updated clinicalStatus and sync
        // with archive=1. No explicit sync call here to avoid duplicate POST requests
        // that cause "Allergy with the same title already exists" errors.

        editDialog.value = false;
        editingAllergy.value = null;
        originalAllergy.value = null;
    });
};

const restoreAllergy = (allergy) => {
    const options = {
        header: 'Restore',
        message: 'Are you sure you want to restore this allergy to active?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        // Set clinicalStatus back to active
        const allergyToRestore = originalAllergy.value || allergy;
        allergyToRestore.clinicalStatus = {
            coding: [
                {
                    code: 'active',
                    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                },
            ],
        };
        await allergiesDP.value.save(allergyToRestore);

        // ScriptSure sync is handled automatically by the DataProvider @saved event
        // (handleAllergySaved), which will pick up the updated clinicalStatus and sync
        // with archive=0. No explicit sync call here to avoid duplicate POST requests
        // that cause "Allergy with the same title already exists" errors.

        editDialog.value = false;
        editingAllergy.value = null;
        originalAllergy.value = null;
    });
};

// '' is not a valid FHIR string, so an empty entry clears the union rather than storing a blank.
const setOnset = (allergy, date) => {
    allergy.onset = date ? { string: date } : undefined;
};

const allergyList = ref([]);
const searchRxNorm = ref(null);
const debouncedSearch = debounce(
    function (val) {
        if (!val) return;

        if (isScriptSureEnabled.value) {
            // Use ScriptSure FDB allergen search for proper drug-allergy interaction codes
            searchAllergen(val)
                .then((results) => {
                    if (!results) return;
                    allergyList.value = results.map((r) => ({
                        display: r.Descr,
                        code: r.allergyId,
                        allergyType: r.allergyType,
                        ROUTED_MED_ID: r.ROUTED_MED_ID,
                        _source: 'fdb',
                    }));
                })
                .catch(() => {
                    // Fallback to RxNorm if ScriptSure search fails
                    getMedByName(val).then((sub) => {
                        if (!sub) return;
                        allergyList.value = sub;
                    });
                });
        } else {
            getMedByName(val).then((sub) => {
                if (!sub) return;
                allergyList.value = sub;
            });
        }
    },
    500,
    { maxWait: 750 },
);
watch(() => searchRxNorm.value, debouncedSearch);

const setAllergyCode = function (allergy, item) {
    if (typeof item === 'string' && item) {
        allergy.code = new CodeableConcept({
            text: item,
            coding: [],
        });
    }

    if (!searchRxNorm.value) return;

    if (item._source === 'fdb') {
        // ScriptSure FDB allergen result - store FDB identifiers for drug-allergy interaction checking
        // Encode allergyType into the code field as "allergyId|allergyType" since FHIR Coding
        // doesn't allow custom properties
        allergy.code.text = item.display;
        allergy.code.coding = [
            {
                code: `${item.code}|${item.allergyType}`,
                system: 'http://fhir.bestnotes.com/scriptsure/fdb',
                display: item.display,
            },
        ];
    } else if (item.code) {
        // RxNorm result (non-ScriptSure fallback)
        allergy.code.coding = [
            {
                code: item.code,
                system: 'http://www.nlm.nih.gov/research/umls/rxnorm',
                display: item?.display,
            },
        ];
        allergy.code.text = item?.display;
    }
};

// Get criticality color
const getCriticalityColor = (criticality) => {
    switch (criticality) {
        case 'high':
            return 'error';
        case 'low':
            return 'success';
        case 'unable-to-assess':
            return 'warning';
        default:
            return 'primary';
    }
};

// Get criticality display text
const getCriticalityDisplay = (criticality) => {
    const item = reactionCriticality.find((r) => r.value === criticality);
    return item?.title || criticality;
};

// The single read of the value the UI labels "Severity". It is FHIR criticality (risk), not reaction
// severity — keeping the read here makes rebinding it to reaction.severity a one-site edit.
const allergySeverity = (allergy) => allergy.criticality;

const allergyReaction = (allergy) => allergy.reaction?.[0]?.manifestation?.[0]?.text || '';

const allergyTypeDisplay = (allergy) => {
    const category = allergy.category?.[0];
    if (!category) return '';
    return allergenTypes.find((type) => type.value === category)?.title || category;
};

// Only .string, deliberately: the edit dialog below reads the same key, so falling back to the
// union's dateTime/date would show a row onset that the dialog opens blank. This widget is the
// only writer of an AllergyIntolerance, and it has only ever written { string: date }.
const allergyOnsetDisplay = (allergy) => formatPartialDate(allergy.onset?.string);

// The tooltip's long form of the same fields. It labels every one of them where the row labels
// only what can't name itself, so the two can't share a single list.
const allergyTooltipLines = (allergy) => {
    const severity = allergySeverity(allergy);
    return [
        { key: 'type', label: 'Type', text: allergyTypeDisplay(allergy) },
        { key: 'severity', label: 'Severity', text: severity ? getCriticalityDisplay(severity) : '' },
        { key: 'reaction', label: 'Reaction', text: allergyReaction(allergy) },
        { key: 'onset', label: 'Onset', text: allergyOnsetDisplay(allergy) },
    ].filter((line) => line.text);
};

// Ordered list of the row's populated fields — absent ones never reach the template, so its
// separator can't dangle. Only a value that can't name itself carries a label: a chip's color and
// a bare date are ambiguous, "Medication" and "Hives" are not.
const allergyRowSegments = (allergy) => {
    const segments = [];

    const type = allergyTypeDisplay(allergy);
    if (type) segments.push({ key: 'type', text: type });

    const severity = allergySeverity(allergy);
    if (isAllergyInactive(allergy)) {
        // A status, not a severity — "Severity Inactive" would misread the row.
        segments.push({ key: 'severity', text: 'Inactive', chipColor: 'grey' });
    } else if (severity) {
        segments.push({ key: 'severity', label: 'Severity', text: getCriticalityDisplay(severity), chipColor: getCriticalityColor(severity) });
    }

    const reaction = allergyReaction(allergy);
    if (reaction) segments.push({ key: 'reaction', text: reaction });

    const onset = allergyOnsetDisplay(allergy);
    if (onset) segments.push({ key: 'onset', label: 'Onset', text: onset });

    return segments;
};

/**
 * Handle DataProvider saved event - automatically sync allergies to ScriptSure
 * This runs after a successful FHIR save without blocking the save operation
 */
const handleAllergySaved = async () => {
    // Only sync if ScriptSure is enabled
    if (!isScriptSureEnabled.value) {
        return;
    }

    // Try to resolve the ScriptSure patient ID, auto-initializing if needed
    let scriptSurePatientId = null;

    if (props.patient) {
        const result = await ensureScriptSurePatient(props.patient);
        if (result && typeof result === 'object' && result.missingFields) {
            scriptSureSnackbarMessage.value = `e-Prescribe sync skipped: missing ${result.missingFields.join(', ')}`;
            scriptSureSnackbar.value = true;
            return;
        }
        scriptSurePatientId = result;
    } else {
        // Fallback: fetch from FHIR (legacy path when patient prop is not provided)
        scriptSurePatientId = await getScriptSurePatientId(patientId.value);
    }

    if (!scriptSurePatientId) {
        return;
    }

    // Use the shared composable to handle syncing
    await handleAllergiesSaved(allergiesData.value, scriptSurePatientId);
};

const setReaction = (allergy, val) => {
    allergy.reaction = [
        new AllergyIntoleranceReaction({
            manifestation: [
                {
                    text: val,
                },
            ],
        }),
    ];
};
</script>

<template>
    <v-card variant="flat" :border="false" class="pb-2">
        <div v-if="!isCompact" class="d-flex py-1 text-h2" style="color: #43a047">
            <FontAwesomeIcon size="lg" :icon="['far', 'hand-dots']" class="align-self-center mr-2"></FontAwesomeIcon>
            <v-list-item-title class="text-h2">{{ title }}</v-list-item-title>
        </div>
        <div v-if="!canViewAllergies" :style="!isCompact ? 'border-left: 3px solid #43a047; opacity: 1; padding-left: 8px; color: black' : 'color: black'">
            <div :class="{ inactiveFontSize: isLocked || printView }">
                <div class="text-wrap text-body-1">Permission denied</div>
            </div>
        </div>

        <DataProvider v-else ref="allergiesDP" v-model="allergiesData" collection autosave refresh-on-change v-bind="allergiesQuery" @loaded="allergiesLoaded" @saved="handleAllergySaved">
            <template #loading>
                <v-skeleton-loader class="mx-auto" width="100%" type="list-item-three-line" />
            </template>
            <template #error="{ error, refresh }">
                <div>
                    {{ error }}
                    <v-btn @click="refresh">Try Again</v-btn>
                </div>
            </template>
            <template #default>
                <div data-cy="allergiesList" data-linkid="allergiesListLinkId">
                    <v-radio-group
                        v-if="!isCompact"
                        v-model="hasAllergies"
                        :disabled="viewOnlyAllergies"
                        :readonly="readOnly"
                        label="Do you have any known allergies?"
                        inline
                        class="mt-0 mb-4"
                        hide-details
                    >
                        <v-radio label="Yes" :value="true" class="mr-2" data-cy="hasAllergiesTrueRadio"></v-radio>
                        <v-radio label="No" :value="false" data-cy="hasAllergiesFalseRadio"></v-radio>
                    </v-radio-group>

                    <!-- Allergies list -->
                    <div :style="!isCompact ? 'border-left: 3px solid #43a047; opacity: 1; padding-left: 8px' : ''">
                        <template v-if="displayedAllergies.length">
                            <v-list lines="one" class="pa-0 bg-transparent">
                                <template v-for="(allergy, index) in limitedAllergies" :key="allergy.id || index">
                                    <v-divider v-if="index"></v-divider>
                                    <!-- In the note the green bar already insets the row, so v-list-item's own 16px gutter
                                         only holds empty space; the compact card keeps it as its inset from the card edge. -->
                                    <v-list-item :data-cy="'allergyItem-' + index" :class="{ 'cursor-pointer': !viewOnlyAllergies && !readOnly, 'px-0': !isCompact }" @click="openEditDialog(allergy)">
                                        <div class="d-flex flex-wrap align-center ga-1">
                                            <v-tooltip open-delay="500" location="right">
                                                <template #activator="{ props: tooltipProps }">
                                                    <!-- Flex items default to min-width:auto, which would let a long allergen
                                                         overflow the row instead of ellipsizing. -->
                                                    <div v-bind="tooltipProps" class="text-truncate font-weight-bold" style="min-width: 0">
                                                        {{ allergy.code?.text }}
                                                    </div>
                                                </template>
                                                <div class="px-2">{{ allergy.code?.text }}</div>
                                                <div v-for="line in allergyTooltipLines(allergy)" :key="line.key" class="px-2">{{ line.label }}: {{ line.text }}</div>
                                            </v-tooltip>
                                            <!-- De-emphasized to match the risk-flag rows on the same clinical dashboard, so the
                                                 two cards read as one hierarchy rather than two. -->
                                            <template v-for="segment in allergyRowSegments(allergy)" :key="segment.key">
                                                <span class="text-body-2 text-medium-emphasis"> · </span>
                                                <!-- One flex item per segment, so the row's gap falls between segments rather
                                                     than inside a label-value pair. -->
                                                <span class="d-flex align-center text-body-2 text-medium-emphasis">
                                                    <!-- Non-breaking: Vue's condense mode drops a plain trailing space. -->
                                                    <template v-if="segment.label">{{ segment.label }}&nbsp;</template>
                                                    <!-- The chip's own padding sets its text in, so a word-space alone reads tight. -->
                                                    <v-chip v-if="segment.chipColor" size="small" label class="black" :class="{ 'ms-1': segment.label }" :color="segment.chipColor">
                                                        <b>{{ segment.text }}</b>
                                                    </v-chip>
                                                    <template v-else>{{ segment.text }}</template>
                                                </span>
                                            </template>
                                        </div>
                                    </v-list-item>
                                </template>
                            </v-list>
                        </template>

                        <!-- No allergies indicated. The answered-No state counts even before the gate resource lands. -->
                        <template v-else-if="hasNoAllergies || hasAllergies === false">
                            <v-list class="pa-0 bg-transparent">
                                <v-list-item class="d-flex justify-center align-center" title="No known allergies." />
                            </v-list>
                        </template>

                        <!-- Empty state for compact mode - hint to add -->
                        <template v-else-if="isCompact && !displayedAllergies.length">
                            <v-card :border="false" flat class="d-flex text-caption pa-2 flex-grow-1">
                                <div class="ml-auto">
                                    Add allergy
                                    <FontAwesomeIcon :icon="['far', 'level-up']" class="ml-2 mr-16" />
                                </div>
                            </v-card>
                        </template>

                        <!-- Empty state for default variant -->
                        <template v-else-if="!isCompact && !displayedAllergies.length && hasAllergies">
                            <v-list class="pa-0 bg-transparent">
                                <v-list-item class="d-flex justify-center align-center" title="No allergies recorded." />
                            </v-list>
                        </template>
                    </div>

                    <!-- Add allergy button (default variant only) -->
                    <v-btn
                        v-if="!isCompact && hasAllergies === true && !viewOnlyAllergies && !readOnly"
                        :disabled="!displayedAllergies.length && activeAllergies.length > 0"
                        color="primary"
                        variant="tonal"
                        size="x-small"
                        class="v-list-item-pointer-hover ml-2 mt-2"
                        data-cy="addAnotherAllergyBtn"
                        @click="openAddDialog"
                    >
                        <template #prepend>
                            <FontAwesomeIcon :icon="['far', 'plus']" size="sm" />
                        </template>
                        Add another allergy
                    </v-btn>
                </div>
            </template>
        </DataProvider>

        <!-- Confirmation Dialog -->
        <BnDialog ref="bnDialogRef" />

        <!-- Add/Edit Allergy Dialog -->
        <v-dialog v-model="editDialog" scrollable persistent :fullscreen="vuetifyDisplay.xs.value" max-width="600px">
            <v-card v-if="editingAllergy" class="d-flex flex-column">
                <ion-header>
                    <ion-toolbar>
                        <v-toolbar density="compact" flat class="flex-grow-0">
                            <v-toolbar-title>{{ isNewAllergy ? 'Add' : 'Edit' }} allergy</v-toolbar-title>
                            <BnToolbarBtn type="text" label="Cancel" icon="times" color="secondary" data-cy="cancelAllergyEdit" @click="cancelEdit" />
                            <BnToolbarBtn type="outlined" right label="Save" icon="check" color="primary" :disabled="!!v$.allergyForm.$errors.length" data-cy="saveAllergy" @click="saveAllergy" />
                            <BnMenu v-if="!isNewAllergy" offset-y left icon="ellipsis-v" data-cy="allergyEditMenu">
                                <BnMenuItem label="Save" icon="check" :disabled="!!v$.allergyForm.$errors.length" @click="saveAllergy" />
                                <BnMenuItem label="Cancel" icon="times" @click="cancelEdit" />
                                <BnMenuItem v-if="isEditingAllergyInactive" icon="inbox-out" label="Restore allergy" data-cy="restoreAllergyBtn" @click="restoreAllergy(editingAllergy)" />
                                <BnMenuItem v-else icon="archive" label="Archive allergy" data-cy="inactivateAllergyBtn" @click="inactivateAllergy(editingAllergy)" />
                            </BnMenu>
                        </v-toolbar>
                    </ion-toolbar>
                </ion-header>
                <v-divider></v-divider>
                <v-card-text class="pa-4 flex-grow-1 overflow-auto">
                    <div class="d-flex flex-column">
                        <!-- Allergen type and name row -->
                        <div class="d-flex flex-wrap">
                            <v-select
                                v-model="editingAllergy.category[0]"
                                label="Type"
                                :items="allergenTypes"
                                style="width: 170px"
                                class="mr-4"
                                :data-options="JSON.stringify(allergenTypes)"
                                data-cy="allergyTypeSelect"
                            >
                                <template #item="{ item, props: itemProps }">
                                    <v-list-item v-bind="itemProps" :title="null">
                                        <template #prepend>
                                            <FontAwesomeIcon :icon="item.raw.icon" class="mr-2" />
                                        </template>
                                        <v-list-item-title>{{ item.raw.title }}</v-list-item-title>
                                    </v-list-item>
                                </template>

                                <template #selection="{ item }">
                                    <FontAwesomeIcon class="mr-2" size="sm" :icon="item.raw.icon" />
                                    {{ item.raw.title }}
                                </template>
                            </v-select>

                            <v-combobox
                                v-model:search="searchRxNorm"
                                :model-value="editingAllergy.code?.text"
                                :items="allergyList"
                                :data-options="JSON.stringify(allergyList)"
                                :menu-props="{ closeOnContentClick: true }"
                                item-title="display"
                                return-object
                                label="Allergen"
                                class="flex-grow-1"
                                style="min-width: 200px"
                                data-cy="allergyAllergenField"
                                v-bind="$bnVuelidateErrorExtractor(v$.allergyForm, 'allergenName', true)"
                                @update:model-value="(item) => setAllergyCode(editingAllergy, item)"
                            >
                                <template #item="{ item, props: itemProps }">
                                    <v-list-item v-bind="itemProps" :title="null">
                                        <v-list-item-title data-cy="allergySearchItem">{{ item.title }}</v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-combobox>
                        </div>

                        <!-- Reaction and severity row -->
                        <div class="d-flex flex-wrap">
                            <v-text-field
                                :model-value="editingAllergy.reaction?.[0]?.manifestation?.[0]?.text"
                                :disabled="!editingAllergy.code?.text"
                                label="Reaction"
                                class="flex-grow-1 mr-2"
                                style="min-width: 200px"
                                data-cy="allergyReactionField"
                                @update:model-value="(val) => setReaction(editingAllergy, val)"
                            ></v-text-field>

                            <v-select
                                v-model="editingAllergy.criticality"
                                :items="reactionCriticality"
                                label="Severity"
                                :disabled="!editingAllergy.code?.text"
                                style="width: 170px"
                                clearable
                                :data-options="JSON.stringify(reactionCriticality)"
                                data-cy="allergySeveritySelect"
                            ></v-select>
                        </div>

                        <!-- Onset date -->
                        <div style="width: 200px">
                            <BnDatePicker
                                :model-value="editingAllergy.onset?.string"
                                :disabled="!editingAllergy.code?.text"
                                :strict-date="false"
                                open-with-icon
                                label="Onset"
                                data-cy="allergyOnsetField"
                                @update:model-value="(val) => setOnset(editingAllergy, val)"
                            ></BnDatePicker>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- ScriptSure missing data notification -->
        <v-snackbar v-model="scriptSureSnackbar" :timeout="6000" color="warning" location="bottom" multi-line>
            {{ scriptSureSnackbarMessage }}
            <template #actions>
                <v-btn variant="outlined" color="white" @click="scriptSureSnackbar = false">Close</v-btn>
            </template>
        </v-snackbar>
    </v-card>
</template>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>
